'use client'

import { observeClientOperation } from '@brew/observability/browser'
import { observabilityContextFromRecord } from '@brew/observability/core'
import type { MetricOutcome } from '@brew/observability/metrics'
import { BROWSER_OPERATION_RUNTIME_CONTRACTS } from './browser-operation.runtime.generated'
import {
  classifyChannelLinkOutcome,
  classifyProductMutationResult,
} from './product-result'

type Contract = {
  domain: string
  surface: string
  phase: string
  transport: 'server_action' | 'convex' | 'clerk' | 'http' | 'browser'
  result?:
    | 'email_persist'
    | 'verification'
    | 'contact_create'
    | 'upload_batch'
    | 'upload_index'
    | 'channel_link'
}

/** Literal request contracts: completion is the named acknowledgement, not a later background job. */
export const BROWSER_OPERATION_CONTRACTS = {
  'email.group.rename': {
    domain: 'email',
    surface: 'email_canvas',
    phase: 'persisted',
    transport: 'server_action',
  },
  'email.layout.persist': {
    domain: 'email',
    surface: 'email_canvas',
    phase: 'persisted',
    transport: 'convex',
  },
  'email.clone': {
    domain: 'email',
    surface: 'email_inventory',
    phase: 'persisted',
    transport: 'server_action',
  },
  'email.delete': {
    domain: 'email',
    surface: 'email_inventory',
    phase: 'persisted',
    transport: 'server_action',
  },
  'email.group.move': {
    domain: 'email',
    surface: 'email_inventory',
    phase: 'persisted',
    transport: 'server_action',
  },
  'email.subject.persist': {
    domain: 'email',
    surface: 'send_dialog',
    phase: 'persisted',
    transport: 'server_action',
    result: 'email_persist',
  },
  'email.preview.persist': {
    domain: 'email',
    surface: 'send_dialog',
    phase: 'persisted',
    transport: 'server_action',
    result: 'email_persist',
  },
  'email.subject.generate': {
    domain: 'email',
    surface: 'send_dialog',
    phase: 'generated',
    transport: 'server_action',
  },
  'email.preview.generate': {
    domain: 'email',
    surface: 'send_dialog',
    phase: 'generated',
    transport: 'server_action',
  },
  'send.contacts.query': {
    domain: 'sending',
    surface: 'send_dialog',
    phase: 'response',
    transport: 'server_action',
  },
  'send.contacts.summary': {
    domain: 'sending',
    surface: 'send_dialog',
    phase: 'response',
    transport: 'server_action',
  },
  'send.variables.coverage': {
    domain: 'sending',
    surface: 'send_dialog',
    phase: 'response',
    transport: 'server_action',
  },
  'audience.counts.refresh': {
    domain: 'audience',
    surface: 'audience_grid',
    phase: 'response',
    transport: 'server_action',
  },
  'contact.create': {
    domain: 'audience',
    surface: 'contact_add',
    phase: 'persisted',
    transport: 'server_action',
    result: 'contact_create',
  },
  'contact.update': {
    domain: 'audience',
    surface: 'contact_sheet',
    phase: 'persisted',
    transport: 'server_action',
  },
  'contact.field.define': {
    domain: 'audience',
    surface: 'contact_sheet',
    phase: 'persisted',
    transport: 'server_action',
  },
  'audience.field.define': {
    domain: 'audience',
    surface: 'audience',
    phase: 'persisted',
    transport: 'server_action',
  },
  'domain.create': {
    domain: 'domains',
    surface: 'domain_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'domain.delete': {
    domain: 'domains',
    surface: 'domain_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'domain.settings.persist': {
    domain: 'domains',
    surface: 'domain_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'domain.verify': {
    domain: 'domains',
    surface: 'domain_settings',
    phase: 'provider_response',
    transport: 'server_action',
    result: 'verification',
  },
  'domain.share.create': {
    domain: 'domains',
    surface: 'domain_settings',
    phase: 'credential_ready',
    transport: 'server_action',
  },
  'domain.share.verify': {
    domain: 'domains',
    surface: 'domain_share',
    phase: 'provider_response',
    transport: 'server_action',
    result: 'verification',
  },
  'domain.entri.prepare': {
    domain: 'domains',
    surface: 'domain_settings',
    phase: 'credential_ready',
    transport: 'server_action',
  },
  'organization.switch': {
    domain: 'settings',
    surface: 'organization_switcher',
    phase: 'session_ready',
    transport: 'clerk',
  },
  'organization.create': {
    domain: 'settings',
    surface: 'organization_create',
    phase: 'persisted',
    transport: 'clerk',
  },
  'organization.rename': {
    domain: 'settings',
    surface: 'team_settings',
    phase: 'persisted',
    transport: 'clerk',
  },
  'organization.logo.persist': {
    domain: 'settings',
    surface: 'team_settings',
    phase: 'persisted',
    transport: 'clerk',
  },
  'organization.leave': {
    domain: 'settings',
    surface: 'team_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'organization.memberships.query': {
    domain: 'settings',
    surface: 'team_settings',
    phase: 'response',
    transport: 'clerk',
  },
  'organization.invitation.accept': {
    domain: 'settings',
    surface: 'organization_switcher',
    phase: 'persisted',
    transport: 'clerk',
  },
  'team.member.role': {
    domain: 'settings',
    surface: 'team_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'team.member.remove': {
    domain: 'settings',
    surface: 'team_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'team.invitation.revoke': {
    domain: 'settings',
    surface: 'team_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'account.profile.persist': {
    domain: 'settings',
    surface: 'account_settings',
    phase: 'persisted',
    transport: 'clerk',
  },
  'account.avatar.persist': {
    domain: 'settings',
    surface: 'account_settings',
    phase: 'persisted',
    transport: 'clerk',
  },
  'api_key.brands.page': {
    domain: 'settings',
    surface: 'api_settings',
    phase: 'response',
    transport: 'server_action',
  },
  'api_key.create': {
    domain: 'settings',
    surface: 'api_settings',
    phase: 'credential_ready',
    transport: 'server_action',
  },
  'api_key.revoke': {
    domain: 'settings',
    surface: 'api_settings',
    phase: 'persisted',
    transport: 'server_action',
  },
  'api_key.rotate': {
    domain: 'settings',
    surface: 'api_settings',
    phase: 'credential_ready',
    transport: 'server_action',
  },
  'channel.link': {
    domain: 'integrations',
    surface: 'channel_link',
    phase: 'persisted',
    transport: 'server_action',
    result: 'channel_link',
  },
  'chat.stop.request': {
    domain: 'chat',
    surface: 'chat_composer',
    phase: 'accepted',
    transport: 'convex',
  },
  'email.preview.schedule': {
    domain: 'email',
    surface: 'email_groups',
    phase: 'accepted',
    transport: 'convex',
  },
  'email.preview.compose': {
    domain: 'email',
    surface: 'email_groups',
    phase: 'response',
    transport: 'server_action',
  },
  'upload.image.prepare': {
    domain: 'uploads',
    surface: 'image_upload',
    phase: 'staged',
    transport: 'http',
  },
  'upload.image.import': {
    domain: 'uploads',
    surface: 'image_upload',
    phase: 'staged',
    transport: 'http',
  },
  'upload.brand.submit': {
    domain: 'uploads',
    surface: 'brand_library',
    phase: 'accepted',
    transport: 'http',
    result: 'upload_index',
  },
  'upload.brand.session': {
    domain: 'uploads',
    surface: 'brand_library',
    phase: 'session_ready',
    transport: 'http',
  },
  'upload.brand.batch': {
    domain: 'uploads',
    surface: 'brand_library',
    phase: 'staged_and_submitted',
    transport: 'http',
    result: 'upload_batch',
  },
} as const satisfies Record<string, Contract>

export type BrowserOperation = keyof typeof BROWSER_OPERATION_CONTRACTS

function classifyResult(contract: Contract, value: unknown): MetricOutcome {
  const outcome = classifyProductMutationResult(value)
  if (outcome !== 'success' || !value || typeof value !== 'object') {
    return outcome
  }
  if (
    contract.result === 'channel_link' &&
    'ok' in value &&
    value.ok === true
  ) {
    return classifyChannelLinkOutcome(
      'value' in value ? value.value : undefined
    )
  }
  if (
    contract.result === 'email_persist' &&
    'skipped' in value &&
    value.skipped === 'pinned-version'
  ) {
    return 'skipped'
  }
  if (
    contract.result === 'verification' &&
    'verified' in value &&
    value.verified === false
  ) {
    return 'partial'
  }
  if (
    contract.result === 'contact_create' &&
    'validation' in value &&
    value.validation === 'unavailable'
  ) {
    return 'partial'
  }
  if (
    contract.result === 'upload_index' &&
    'accepted' in value &&
    value.accepted === 0 &&
    !('started' in value && value.started === true)
  ) {
    return 'skipped'
  }
  if (contract.result === 'upload_batch') {
    const batch = value as {
      staged?: Array<{ status?: string }>
      submit?: { failedBatches?: number; failedFiles?: number }
    }
    // Inspect settlement metadata only, with a hard work bound; never files,
    // names, URLs, image data or rejection messages.
    if (!Array.isArray(batch.staged) || batch.staged.length > 10_000) {
      return 'unknown'
    }
    let failures = 0
    for (const settled of batch.staged) {
      if (settled.status === 'rejected') {
        failures += 1
      }
    }
    if (batch.staged.length === 0) {
      return 'skipped'
    }
    if (failures === batch.staged.length) {
      return 'failure'
    }
    if (
      failures > 0 ||
      (batch.submit?.failedBatches ?? 0) > 0 ||
      (batch.submit?.failedFiles ?? 0) > 0
    ) {
      return 'partial'
    }
  }
  return outcome
}

/** Only explicit canonical context is read; arguments/results never become telemetry fields. */
export function observeBrowserOperation<T>(
  operation: BrowserOperation,
  callback: () => Promise<T>,
  context?: unknown
): Promise<T> {
  const contract = BROWSER_OPERATION_RUNTIME_CONTRACTS[operation]
  return observeClientOperation(
    `client.${operation}`,
    {
      tags: {
        domain: contract.domain,
        surface: contract.surface,
        phase: contract.phase,
        transport: contract.transport,
      },
      context: observabilityContextFromRecord(context),
      classifyResult: (result) => classifyResult(contract, result),
    },
    callback
  )
}
